import { SearchOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getCountryFromCode } from 'common/helpers/country';
import generatePath from '~settings/routing';
import { TableSearchInput, TableAsyncSelect } from 'common/components/table-filter-component';
import OngoingLeads from './ongoingLeads/index';
import NoteModal from '../noteModal/index';
import { EmptyStudent } from 'common/components/svgs';
import { dayFormatType, formatDay } from 'common/helpers/day';
import { useStudentList } from './hooks';
import { getNotes } from './helpers';

const StudentList = () => {
  const {
    loading,
    studentId,
    landlordId,
    studentsList,
    listTotalCount,
    pagination,
    queryFilter,
    showListHeaderFilters,
    isValidFilter,
    isModalOpen,
    searchCountryLoading,
    searchCountryResult,
    removeSpace,
    setPagination,
    setQueryFilter,
    setShowListHeaderFilters,
    handleAddNoteClick,
    handleAddNoteClose,
    handleAddedNote,
    handleGoToCheck,
  } = useStudentList();
  const { t } = useTranslation();
  const history = useHistory();

  const tableColumns = [
    {
      title: t('cms:cms.students.list.column.name'),
      dataIndex: 'fullName',
      key: 'name',
      className: 'students-page__body__list__column',
      width: 150,
      fixed: 'left',
      render: text => (
        <span
          title={text}
          className="students-page__body__list__column-body"
          style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
        >
          {text || '-'}
        </span>
      ),
      filterDropdown: (
        <div className="students__body__list__columns-dropdown">
          <TableSearchInput
            field="partnerName"
            value={queryFilter.fullName || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, fullName: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowListHeaderFilters(prev => ({ ...prev, fullName: false }));
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.fullName ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showListHeaderFilters.fullName,
      onFilterDropdownVisibleChange: view => {
        setShowListHeaderFilters(prev => ({ ...prev, fullName: view }));
      },
    },
    {
      title: (
        <div className="students-page__body__list__column-header">
          {t('cms:cms.students.list.column.email')}
        </div>
      ),
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      className: '',
      width: 220,
      render: text => (
        <span title={text} className="students-page__body__list__column-body">
          {text ? text : '-'}
        </span>
      ),
      filterDropdown: (
        <div className="students__body__list__columns-dropdown">
          <TableSearchInput
            field="emailAddress"
            value={queryFilter.emailAddress || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, emailAddress: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowListHeaderFilters(prev => ({ ...prev, emailAddress: false }));
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.emailAddress ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showListHeaderFilters.emailAddress,
      onFilterDropdownVisibleChange: view => {
        setShowListHeaderFilters(prev => ({ ...prev, emailAddress: view }));
      },
    },
    {
      title: (
        <div className="students-page__body__list__column-header">
          {t('cms:cms.students.list.column.phone_number')}
        </div>
      ),
      dataIndex: 'registeredPhone',
      key: 'registeredPhone',
      className: '',
      width: 180,
      render: text => (
        <span title={text} className="students-page__body__list__column-body">
          {text ? text : '-'}
        </span>
      ),
      filterDropdown: (
        <div className="students__body__list__columns-dropdown">
          <TableSearchInput
            field="registeredPhone"
            value={queryFilter.registeredPhone || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, registeredPhone: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowListHeaderFilters(prev => ({ ...prev, registeredPhone: false }));
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.registeredPhone ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showListHeaderFilters.registeredPhone,
      onFilterDropdownVisibleChange: view => {
        setShowListHeaderFilters(prev => ({ ...prev, registeredPhone: view }));
      },
    },
    {
      title: (
        <div className="students-page__body__list__column-header">
          {t('cms:cms.students.list.column.source_country')}
        </div>
      ),
      dataIndex: 'sourceCountry',
      key: 'sourceCountry',
      className: '',
      width: 220,
      render: text => (
        <span title={text} className="students-page__body__list__column-body">
          {getCountryFromCode(text)}
        </span>
      ),
      filterDropdown: (
        <div className="students__body__list__columns-dropdown">
          <TableAsyncSelect
            field="sourceCountry"
            isMulti
            loading={searchCountryLoading}
            value={queryFilter.sourceCountryCodes || []}
            list={searchCountryResult}
            onInput={value =>
              setQueryFilter(current => ({ ...current, countrySearchKeyword: value }))
            }
            fetching={searchCountryLoading}
            onChange={(_fieldName, value) => {
              const countryIds = value.length ? value : [];
              setQueryFilter(current => ({ ...current, sourceCountryCodes: countryIds }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowListHeaderFilters(prev => ({ ...prev, sourceCountryCodes: false }));
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined
          style={{ color: queryFilter.sourceCountryCodes.length ? '#38b2a6' : '#c8c9cb' }}
        />
      ),
      filterDropdownVisible: showListHeaderFilters.sourceCountryCodes,
      onFilterDropdownVisibleChange: view => {
        setShowListHeaderFilters(prev => ({ ...prev, sourceCountryCodes: view }));
      },
    },
    {
      title: (
        <div className="students-page__body__list__column-header">
          {t('cms:cms.students.list.column.ongoing_leads')}
        </div>
      ),
      dataIndex: 'ongoingOrders',
      key: 'ongoingOrders',
      width: 130,
      render: (data, record) => <OngoingLeads studentId={record.id} data={data} />,
    },
    {
      title: (
        <div className="students-page__body__list__column-header">
          {t('cms:cms.students.list.column.notes')}
        </div>
      ),
      dataIndex: 'latestNote',
      key: 'latestNote',
      width: 220,
      render: text => (
        <span title={text} className="students-page__body__list__column-body">
          {getNotes(text, 50)}
        </span>
      ),
    },
    {
      title: t('cms:cms.students.list.column.create_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: 'students-page__body__list__column students-page__body__list__column--min-w-180',
      render: text => {
        return (
          <span className="students-page__body__list__column-body students-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
    },
    {
      title: t('cms:cms.students.list.column.update_date'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      className: 'students-page__body__list__column students-page__body__list__column--min-w-180',
      render: text => {
        return (
          <span className="students-page__body__list__column-body students-page__body__list__column--min-w-180">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
    },
    {
      title: t('cms:cms.students.list.column.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_text, record) => (
        <div className="students-page__body__list__column-body">
          <a
            className="students-page__column-action-link"
            role="button"
            tabIndex="0"
            onClick={e => {
              handleAddNoteClick(e, record);
            }}
          >
            {t('cms:cms.students.list.column.actions.add_note')}
          </a>
        </div>
      ),
    },
  ];

  return (
    <div className="students-page">
      <div className="students-page__header">
        <h1>{t('cms:cms.students.title')}</h1>
      </div>
      <section
        className={classNames('students-page__body', {
          'students-page__body--bg-white': studentsList.length === 0 && !loading,
        })}
      >
        <Choose>
          <When condition={studentsList.length || isValidFilter || loading}>
            <Table
              rowKey={record => record.id}
              columns={tableColumns}
              className="table-list students-page__body__list"
              dataSource={studentsList}
              pagination={{
                current: pagination.pageNumber,
                pageSize: pagination.pageSize,
                showSizeChanger: true,
                hideOnSinglePage: false,
                total: listTotalCount,
              }}
              loading={loading}
              onChange={(newestPagination, filter) => {
                if (newestPagination.pageSize !== pagination.pageSize) {
                  setPagination({
                    pageNumber: 1,
                    pageSize: newestPagination.pageSize,
                  });
                } else {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                }
                setQueryFilter(current => ({
                  ...current,
                  enabled: filter.status?.length ? filter.status[0] === 'true' || false : null,
                }));
              }}
              onRow={record => {
                return {
                  onClick: () => {
                    history.push(
                      generatePath('students.detail', { id: record.id, ongoing: false }),
                    );
                  },
                };
              }}
            />
          </When>
          <When condition={studentsList.length === 0 && !isValidFilter && !loading}>
            <div className="students-page__blank-tips">
              <div className="students-page__blank-tips__content">
                <div>
                  <EmptyStudent />
                </div>
                <span className="students-page__blank-tips__tip">
                  {t('cms:cms.students.list.blankTip')}
                </span>
              </div>
            </div>
          </When>
        </Choose>
        {isModalOpen && (
          <NoteModal
            isOpen={isModalOpen}
            studentId={studentId}
            landlordId={landlordId}
            onClose={handleAddNoteClose}
            onAdded={handleAddedNote}
            successMessage={
              <div>
                {t('cms:cms.students.list.modal.note_success')}
                {` `}
                <a
                  onClick={e => {
                    handleGoToCheck(e, studentId);
                  }}
                  href="#"
                >
                  {t('cms:cms.students.list.modal.go_to_check')}
                </a>
              </div>
            }
          />
        )}
      </section>
    </div>
  );
};

export default StudentList;
